/**
 * 管理者向けダッシュボード(トップ画面)の表示データ。2026-09-12 の指示に沿った構成。
 * **2026-09-17 MTG: 期間(日/週/月/任意期間)を選べるようにした**。取得ペース・着地は「月」のときだけ出す(月の目標に対する値のため)。
 * 警告バーは期間に関係なく「今日から直近7日」。
 *
 * **表示は全て「集計データがある最新日までの確定値」**。BlueBean の CSV は翌朝取り込むため、
 * 当日の進捗はそもそも持っていない。リアルタイム風の見せ方はしない。
 *
 * 計算はこの1か所に集約し、画面(HomePage)は並べるだけにする。金額は PL と同じ `buildPl` の結果を渡す。
 * まだ元データが無い項目は `null` を返し、画面では「—」と出す(ごまかさない)。
 */
import { priceOn, type Company, type DailySummary, type DateStr, type Delivery, type MonthTargets } from '@core/lib/types';
import { addDays, apphOf, bbHoursOf, categoryRows, costRate, daysInMonth, derived, groupByAppointer, rankOf, sumAgg, type Aggregate, type Rank, type StaffMetrics } from './calc';
import type { ExpenseDaily } from './expenseRepo';

export type Severity = 'high' | 'mid';

export type DashboardAlert = {
  key: string;
  severity: Severity;
  label: string;
  detail: string;
  to: string;
};

/** ① 警告バー。数字は「直近7日のうち、その状態の日数」 */
export type WarnCounts = { csv: number; delivery: number; expense: number; kintai: number };

/** ② 取得ペース */
export type Pace = {
  target: number | null;
  actual: number;
  /** 残りを埋めるのに必要な1日あたり */
  needPerDay: number | null;
  /** いまの1日あたり */
  nowPerDay: number | null;
  /** 不足(必要−現在)。マイナスなら前倒し */
  gap: number | null;
  elapsed: number;
  total: number;
};

/** ② 原価率(管理者込み / 管理者抜き) */
export type CostView = {
  rate: number | null;
  target: number;
  /** 目標との差(pt)。プラスが超過 */
  over: number | null;
  /** 目標まで下げるのに減らす金額 */
  cut: number | null;
};

/** ④ 取得→納品 */
export type Flow = {
  prevDate: DateStr | null;
  prevAppointments: number;
  date: DateStr | null;
  delivered: number;
  rate: number | null;
  /** 会社別の未納品在庫(今月の取得累計 − 納品累計) */
  stock: { company_id: string; name: string; stock: number }[];
};

/** ⑤ 会社別採算 */
export type CompanyLine = {
  company_id: string;
  name: string;
  appointments: number;
  delivered: number | null;
  deliveryRate: number | null;
  /** 取得率。1人が2社に架電した日は通話回数が1社目に寄るため参考値 */
  hitRate: number | null;
  costPerDelivery: number | null;
  gross: number;
  grossPerHour: number | null;
  costRate: number | null;
  cancelRate: number | null;
  stock: number | null;
};

/** ⑥ オペレーター */
export type OpLine = {
  op_id: string;
  name: string;
  rank: Rank;
  /** 原価率(報酬額 ÷ 売上見込)。RANK の右隣に出す(2026-09-17 MTG) */
  costRate: number | null;
  apph: number | null;
  /** 直近7日平均に対する比(%)。100 が平均どおり */
  vs7d: number | null;
  afterAvg: number | null;
  pay: number;
  reason: string;
};

/** ⑦ 雇用形態別 */
export type StyleLine = { key: string; label: string; apph: number | null; payPerAppointment: number | null; costRate: number | null; people: number };

export type HourLine = { hour: number; appointments: number; perPerson: number | null };

/** 表示する期間。mode=month のときだけ取得ペース・着地を計算する */
export type DashPeriod = { mode: 'day' | 'week' | 'month' | 'range'; from: DateStr; to: DateStr; label: string };

export type DashboardData = {
  /** 表示の基準日(期間内で集計データがある最新日)。これより後の数字は持っていない */
  asOf: DateStr | null;
  period: DashPeriod;
  warn: WarnCounts;
  warnDays: { key: keyof WarnCounts; dates: DateStr[] }[];
  pace: Pace;
  costWith: CostView;
  costEx: CostView;
  money: { sales: number; gross: number; op: number; salesF: number | null; grossF: number | null; opF: number | null };
  alerts: DashboardAlert[];
  flow: Flow;
  companies: CompanyLine[];
  watchOps: OpLine[];
  topOps: OpLine[];
  styles: StyleLine[];
  ranks: { rank: Rank; count: number }[];
  lowRankPay: number;
  hours: HourLine[];
  /** 稼働人数。1日のときはその日の人数、複数日のときは延べ人数 */
  people: number;
  peopleIsTotal: boolean;
  trend: { labels: string[]; appointments: (number | null)[]; paceLine: (number | null)[]; costRate: (number | null)[]; costTarget: number; sales: (number | null)[]; gross: (number | null)[] };
};

export type BuildArgs = {
  period: DashPeriod;
  today: DateStr;
  /** 読み込んだ範囲の全行(期間の前6日を含む。7日平均比に使う) */
  allRows: StaffMetrics[];
  deliveries: Delivery[];
  /** 期間の最後の月の目標(原価率目標・日次目標) */
  targets: MonthTargets;
  companies: Company[];
  /** 期間合計の PL(buildPl の結果)。金額は PL と同じ値を使う */
  pl: { salesSubtotal: number; gross: number; expenseTotal: number; appointments: number } | null;
  /** 期間内の日ごとの PL */
  plByDate: Map<DateStr, { salesSubtotal: number; gross: number; appointments: number }>;
  /** 警告バー用(今日から直近7日。期間とは別に読む) */
  warn: { summaries: DailySummary[]; deliveries: Delivery[]; expenses: ExpenseDaily };
};

const pct = (a: number, b: number): number | null => (b > 0 ? (a / b) * 100 : null);

/** 直近7日で「その状態だった日」を数える(①) */
function warnOf(args: BuildArgs) {
  const { today } = args;
  const { summaries, deliveries, expenses } = args.warn;
  const byDate = new Map(summaries.map((s) => [s.date, s]));
  const delivered = new Set(deliveries.map((d) => d.date));
  const out: Record<keyof WarnCounts, DateStr[]> = { csv: [], delivery: [], expense: [], kintai: [] };
  for (let i = 6; i >= 0; i--) {
    const date = addDays(today, -i) as DateStr;
    const s = byDate.get(date);
    if (!s || s.staff.length === 0) {
      out.csv.push(date);
      continue;
    }
    if (!delivered.has(date)) out.delivery.push(date);
    const ex = expenses.get(date);
    if (!ex || Object.values(ex).every((v) => !v)) out.expense.push(date);
    const shukkin = s.staff.filter((st) => st.work_style === 'shinagawa' || st.work_style === 'timee').length;
    if (shukkin > 0 && !s.kintai_check) out.kintai.push(date);
  }
  return out;
}

/** 会社ごとの納品数・キャンセル数(今月の合計) */
function deliveryTotals(deliveries: Delivery[]) {
  const count = new Map<string, number>();
  const cancel = new Map<string, number>();
  for (const d of deliveries) {
    for (const [cid, n] of Object.entries(d.counts ?? {})) count.set(cid, (count.get(cid) ?? 0) + n);
    for (const [cid, det] of Object.entries(d.details ?? {})) {
      cancel.set(cid, (cancel.get(cid) ?? 0) + (det.cancel_zaitaku ?? 0) + (det.cancel_shukkin ?? 0));
    }
  }
  return { count, cancel };
}

export function buildDashboard(args: BuildArgs): DashboardData {
  const { period, today, allRows, deliveries, targets, companies, pl, plByDate } = args;
  const inPeriod = (date: string) => date >= period.from && date <= period.to;
  const isMonth = period.mode === 'month';
  const month = period.to.slice(0, 7);
  const total = daysInMonth(month);
  // 以下、名前は monthRows のままだが中身は「選んだ期間の行」
  const monthRows = allRows.filter((r) => inPeriod(r.date));
  const monthDeliveries = deliveries.filter((d) => inPeriod(d.date));
  const dates = [...plByDate.keys()].filter(inPeriod).sort();
  const asOf = dates.length > 0 ? (dates[dates.length - 1] ?? null) : null;
  const elapsed = isMonth && asOf ? Number(asOf.slice(8, 10)) : 0;

  // ---- ① 警告 ----
  const w = warnOf(args);
  const warn: WarnCounts = { csv: w.csv.length, delivery: w.delivery.length, expense: w.expense.length, kintai: w.kintai.length };

  // ---- ② 取得ペース ----
  // 目標は月の値なので、月を選んだときだけ比べる(日・週・任意期間は実績だけ)
  const businessDays = targets.business_days ?? total;
  const target = isMonth && targets.daily_appointments > 0 ? targets.daily_appointments * businessDays : null;
  const actual = pl?.appointments ?? 0;
  const remainDays = Math.max(0, total - elapsed);
  const nowPerDay = isMonth && elapsed > 0 ? actual / elapsed : null;
  const needPerDay = target !== null ? (remainDays > 0 ? Math.max(0, target - actual) / remainDays : 0) : null;
  const pace: Pace = { target, actual, needPerDay, nowPerDay, gap: needPerDay !== null && nowPerDay !== null ? needPerDay - nowPerDay : null, elapsed, total };

  // ---- ② 原価率 ----
  const cats = categoryRows(monthRows);
  const viewOf = (agg: Aggregate, t: number): CostView => {
    const rate = costRate(agg);
    const over = rate === null ? null : rate - t;
    return { rate, target: t, over, cut: over !== null && over > 0 ? Math.round(agg.pay - (agg.revenue * t) / 100) : null };
  };
  const costWith = viewOf(cats.with_manager, targets.cost_rate_with_manager);
  const costEx = viewOf(cats.no_manager, targets.cost_rate_total);

  // ---- ② 金額 ----
  const sales = pl?.salesSubtotal ?? 0;
  const gross = pl?.gross ?? 0;
  const op = gross - (pl?.expenseTotal ?? 0);
  // 着地(月末の見込み)も月を選んだときだけ
  const scale = isMonth && elapsed > 0 ? total / elapsed : null;
  const money = {
    sales,
    gross,
    op,
    salesF: scale === null ? null : Math.round(sales * scale),
    grossF: scale === null ? null : Math.round(gross * scale),
    opF: scale === null ? null : Math.round(op * scale),
  };

  // ---- ④ 取得 → 納品(取得日と納品日を1日ずらす) ----
  const { count: delCount, cancel: delCancel } = deliveryTotals(monthDeliveries);
  const apptByDate = new Map<string, number>();
  for (const [d, p] of plByDate) apptByDate.set(d, p.appointments);
  const lastDelivery = monthDeliveries.map((d) => d.date).sort().at(-1) ?? null;
  const prevDate = lastDelivery ? (addDays(lastDelivery as DateStr, -1) as DateStr) : null;
  const deliveredOnLast = lastDelivery ? Object.values(monthDeliveries.find((d) => d.date === lastDelivery)?.counts ?? {}).reduce((a, b) => a + b, 0) : 0;
  const prevAppointments = prevDate ? (apptByDate.get(prevDate) ?? 0) : 0;
  const apptByCompany = new Map<string, number>();
  for (const r of monthRows) if (r.company_id) apptByCompany.set(r.company_id, (apptByCompany.get(r.company_id) ?? 0) + r.appointments);
  const nameOf = (cid: string) => companies.find((c) => c.id === cid)?.name ?? cid;
  const flow: Flow = {
    prevDate,
    prevAppointments,
    date: lastDelivery as DateStr | null,
    delivered: deliveredOnLast,
    rate: pct(deliveredOnLast, prevAppointments),
    stock: [...apptByCompany.entries()]
      .map(([cid, n]) => ({ company_id: cid, name: nameOf(cid), stock: n - (delCount.get(cid) ?? 0) }))
      .filter((x) => x.stock !== 0)
      .sort((a, b) => b.stock - a.stock),
  };

  // ---- ⑤ 会社別採算 ----
  const byCompany = new Map<string, StaffMetrics[]>();
  for (const r of monthRows) if (r.company_id) byCompany.set(r.company_id, [...(byCompany.get(r.company_id) ?? []), r]);
  const companyLines: CompanyLine[] = [...byCompany.entries()]
    .map(([cid, list]) => {
      const agg = sumAgg(list);
      const c = companies.find((x) => x.id === cid);
      const price = c ? priceOn(c, asOf ?? today) : 0;
      const delivered = delCount.get(cid) ?? null;
      const revenue = delivered !== null && price > 0 ? delivered * price : agg.revenue;
      const g = revenue - agg.pay;
      return {
        company_id: cid,
        name: nameOf(cid),
        appointments: agg.appointments,
        delivered,
        deliveryRate: delivered === null ? null : pct(delivered, agg.appointments),
        hitRate: pct(agg.appointments, agg.calls),
        costPerDelivery: delivered && delivered > 0 ? Math.round(agg.pay / delivered) : null,
        gross: g,
        grossPerHour: agg.real_hours > 0 ? Math.round(g / agg.real_hours) : null,
        costRate: pct(agg.pay, revenue),
        cancelRate: delivered && delivered > 0 ? pct(delCancel.get(cid) ?? 0, delivered) : null,
        stock: delivered === null ? null : agg.appointments - delivered,
      };
    })
    .sort((a, b) => b.appointments - a.appointments);

  // ---- ⑥ オペレーター ----
  const groups = groupByAppointer(monthRows.filter((r) => !r.is_manager));
  /** 直近7日(asOf を含む)の平均 見込/h。比較の分母 */
  const last7 = asOf ? new Set(Array.from({ length: 7 }, (_, i) => addDays(asOf, -i))) : new Set<string>();
  const opLine = (g: (typeof groups)[number], reason: string): OpLine => {
    const recent = sumAgg(g.children.filter((r) => last7.has(r.date)));
    const base = apphOf(recent);
    const now = asOf ? apphOf(sumAgg(g.children.filter((r) => r.date === asOf))) : null;
    return {
      op_id: g.op_id,
      name: g.name,
      rank: g.rank,
      costRate: costRate(g.agg),
      apph: apphOf(g.agg),
      vs7d: base !== null && base > 0 && now !== null ? (now / base) * 100 : null,
      afterAvg: derived(g.agg).after_avg,
      pay: g.agg.pay,
      reason,
    };
  };
  const afterAvgAll = (() => {
    const all = sumAgg(monthRows.filter((r) => !r.is_manager));
    return derived(all).after_avg;
  })();
  const watch: OpLine[] = [];
  for (const g of groups) {
    if (g.agg.appointments === 0 && g.agg.calls === 0) continue;
    const l = opLine(g, '');
    const reasons: string[] = [];
    if (l.rank === 'C' || l.rank === 'B-') reasons.push('RANKが下位');
    if (afterAvgAll !== null && l.afterAvg !== null && l.afterAvg > afterAvgAll * 2) reasons.push('後処理が平均の2倍超');
    if (l.vs7d !== null && l.vs7d <= 75) reasons.push('7日平均比 -25%以上');
    if (reasons.length > 0) watch.push({ ...l, reason: reasons.join(' / ') });
  }
  const watchOps = watch.sort((a, b) => (a.vs7d ?? 999) - (b.vs7d ?? 999)).slice(0, 8);
  const topOps = groups.slice(0, 5).map((g) => opLine(g, ''));

  // ---- ⑦ 雇用形態別 ----
  const styleDefs: { key: string; label: string; f: (m: StaffMetrics) => boolean }[] = [
    { key: 'shinagawa', label: '出勤/時給', f: (m) => m.work_style === 'shinagawa' && !m.is_manager },
    { key: 'zaitaku_jikyu', label: '在宅/時給', f: (m) => m.work_style === 'zaitaku' && m.pay_type === 'hourly' },
    { key: 'buai', label: '在宅/歩合', f: (m) => m.work_style === 'zaitaku' && m.pay_type === 'commission' },
    { key: 'timee', label: '単発バイト', f: (m) => m.work_style === 'timee' },
    { key: 'kanri', label: '管理者', f: (m) => m.is_manager },
  ];
  const styles: StyleLine[] = styleDefs.map((d) => {
    const list = monthRows.filter(d.f);
    const agg = sumAgg(list);
    return {
      key: d.key,
      label: d.label,
      apph: apphOf(agg),
      payPerAppointment: agg.appointments > 0 ? Math.round(agg.pay / agg.appointments) : null,
      costRate: costRate(agg),
      people: new Set(list.map((r) => r.op_id)).size,
    };
  });
  const rankOrder: Rank[] = ['S', 'A', 'B+', 'B-', 'C'];
  const ranks = rankOrder.map((r) => ({ rank: r, count: groups.filter((g) => g.rank === r).length }));
  const lowRankPay = groups.filter((g) => g.rank === 'B-' || g.rank === 'C').reduce((s, g) => s + g.agg.pay, 0);

  // ---- ⑧ 時間帯別(1日ならその日、複数日なら期間の合計。1人あたりは延べ人数で割る) ----
  const dayRows = period.mode === 'day' ? (asOf ? monthRows.filter((r) => r.date === asOf) : []) : monthRows;
  const people = [...new Set(dayRows.map((r) => r.date))].reduce(
    (s, d) => s + new Set(dayRows.filter((r) => r.date === d && (r.calls > 0 || r.appointments > 0)).map((r) => r.bb_id)).size,
    0,
  );
  const hours: HourLine[] = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19].map((h, i) => {
    const n = dayRows.reduce((s, r) => s + (r.by_hour[i] ?? 0), 0);
    return { hour: h, appointments: n, perPerson: people > 0 ? n / people : null };
  });

  // ---- ⑨ 推移 ----
  const labels: string[] = [];
  const tAppt: (number | null)[] = [];
  const tSales: (number | null)[] = [];
  const tGross: (number | null)[] = [];
  const tCost: (number | null)[] = [];
  const tPace: (number | null)[] = [];
  const perDayTarget = target !== null ? target / total : null;
  for (let date = period.from; date <= period.to; date = addDays(date, 1) as DateStr) {
    if (!asOf || date > asOf) break;
    labels.push(isMonth ? String(Number(date.slice(8, 10))) : `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`);
    const p = plByDate.get(date);
    tAppt.push(p ? p.appointments : null);
    tSales.push(p ? p.salesSubtotal : null);
    tGross.push(p ? p.gross : null);
    const dayAgg = sumAgg(monthRows.filter((r) => r.date === date));
    tCost.push(dayAgg.revenue > 0 ? costRate(dayAgg) : null);
    tPace.push(perDayTarget);
  }

  // ---- ③ 要対応アラート ----
  const alerts: DashboardAlert[] = [];
  if (costWith.over !== null && costWith.over > 0) {
    alerts.push({
      key: 'cost',
      severity: costWith.over >= 3 ? 'high' : 'mid',
      label: `原価率 目標比 +${costWith.over.toFixed(1)}pt`,
      detail: costWith.cut !== null ? `目標まで下げるには ¥${costWith.cut.toLocaleString('ja-JP')} の削減が必要` : '',
      to: '/pl?mode=month',
    });
  }
  if (pace.gap !== null && pace.gap > 0) {
    alerts.push({
      key: 'pace',
      severity: pace.gap >= 2 ? 'high' : 'mid',
      label: `取得ペース -${pace.gap.toFixed(1)}件/日`,
      detail: `必要 ${pace.needPerDay?.toFixed(1)}件/日 に対して 現在 ${pace.nowPerDay?.toFixed(1)}件/日`,
      to: '/summary/staff',
    });
  }
  for (const c of companyLines) {
    if (c.deliveryRate !== null && c.deliveryRate < 85 && c.appointments >= 10) {
      alerts.push({ key: `del-${c.company_id}`, severity: 'mid', label: `${c.name} 納品率 ${c.deliveryRate.toFixed(1)}%`, detail: `取得 ${c.appointments}件 に対して納品 ${c.delivered}件`, to: '/summary/companies' });
    }
  }
  const dropped = watchOps.filter((o) => o.vs7d !== null && o.vs7d <= 75);
  if (dropped.length > 0) {
    alerts.push({
      key: 'ops',
      severity: 'mid',
      label: `オペレーター${dropped.length}名 見込取得/h が7日平均比 -25%以上`,
      detail: dropped.slice(0, 3).map((o) => o.name).join('・') + (dropped.length > 3 ? ' ほか' : ''),
      to: '/summary/staff',
    });
  }
  alerts.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1));

  return {
    asOf,
    period,
    warn,
    warnDays: (Object.keys(w) as (keyof WarnCounts)[]).map((k) => ({ key: k, dates: w[k] })),
    pace,
    costWith,
    costEx,
    money,
    alerts: alerts.slice(0, 5),
    flow,
    companies: companyLines,
    watchOps,
    topOps,
    styles,
    ranks,
    lowRankPay,
    hours,
    people,
    peopleIsTotal: period.mode !== 'day',
    trend: { labels, appointments: tAppt, paceLine: tPace, costRate: tCost, costTarget: targets.cost_rate_total, sales: tSales, gross: tGross },
  };
}

/** 使っていない import を残さないための再輸出(画面側で型だけ使う) */
export type { Aggregate, Rank };
export { bbHoursOf, rankOf };
